import { ShiftInviteStatus } from "@clipboard-health/contract-backend-main";
import { isDefined } from "@clipboard-health/util-ts";
import { Box, Skeleton, Stack } from "@mui/material";
import { type ShiftInviteMessageProps } from "@src/appV2/Chat/type";
import { parseShiftInviteChatMessage } from "@src/appV2/Chat/utils";

import { ChatWidgetWrapper } from "../../redesign/Chat/ChatWidgetWrapper";
import { ShiftInviteAlerts } from "../../redesign/WorkerShifts/ShiftInvites/Alerts";
import { useGetShiftInvite } from "../../redesign/WorkerShifts/ShiftInvites/api/useGetShiftInvite";
import { ShiftInviteItemProvider } from "../../redesign/WorkerShifts/ShiftInvites/context/shiftInviteItemContext";
import { ShiftInviteCardContainer } from "../../redesign/WorkerShifts/ShiftInvites/ShiftInviteCardContainer";
import { ShiftInviteCardSkeleton } from "../../redesign/WorkerShifts/ShiftInvites/ShiftInviteCardSkeleton";

export function ShiftInviteRequestMessage(props: ShiftInviteMessageProps) {
  const { agentMessage, shiftInviteId, status } = parseShiftInviteChatMessage(props);

  if (
    !isDefined(agentMessage) ||
    !isDefined(status) ||
    !isDefined(shiftInviteId) ||
    status === ShiftInviteStatus.DELETED
  ) {
    // If we return null ,the message will render as standard sendbird message.
    // This is a custom message but we don't have enough data to render it properly
    return <span />;
  }

  return (
    <ShiftInviteItemProvider>
      <ShiftInviteMessageContent status={status} shiftInviteId={shiftInviteId} />
    </ShiftInviteItemProvider>
  );
}

interface ShiftInviteMessageContentProps {
  shiftInviteId: string;
  status: ShiftInviteStatus;
}

function ShiftInviteMessageContent(props: ShiftInviteMessageContentProps) {
  const { shiftInviteId, status } = props;
  const { data: shiftInviteResponse, isLoading } = useGetShiftInvite({ shiftInviteId });
  const shiftInvite = shiftInviteResponse?.data;
  const facilityName = shiftInvite?.attributes.facility.name;

  if (isLoading) {
    const isCardActionable = status === ShiftInviteStatus.PENDING;
    return (
      <ChatWidgetWrapper>
        <Stack alignItems="center" width="100%">
          {/* Message header */}
          <Box sx={{ paddingY: 5 }}>
            <Skeleton variant="text" width={160} />
          </Box>
          <ShiftInviteCardSkeleton isCardActionable={isCardActionable} />
        </Stack>
      </ChatWidgetWrapper>
    );
  }

  if (!isDefined(shiftInvite)) {
    return <span />;
  }

  return (
    <ChatWidgetWrapper headerText={`${facilityName} sent you a shift invite`}>
      <ShiftInviteCardContainer shiftInvite={shiftInvite} />
      <ShiftInviteAlerts shiftInvite={shiftInvite} />
    </ChatWidgetWrapper>
  );
}
